import { IFaxDocument } from '@dataTypes/fax.type'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { BLOB_FOLDERS } from '@constants/Azure'
import { logger } from '@utils/safeLogger'
import { ApiError, toApiError } from '../errors/api-error'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_FAX')
const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_AZURE')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type GenericResponseData = Record<string, unknown>

type DocumentResponseData = { fax_document?: IFaxDocument }
type DocumentListResponseData = { fax_documents?: IFaxDocument[] }

type FaxUploadMeta = {
  file_url: string
  file_name: string
  mime_type: string
  file_size: number
}

type FaxUploadResponseData = {
  upload?: FaxUploadMeta
  fax_document?: unknown
}

const getHeaders = (dbName?: string): Record<string, string> =>
  dbName ? { ...headers, 'x-db-name': dbName } : headers

const FAX_ATTACHMENTS_FOLDER = BLOB_FOLDERS.FAX_ATTACHMENTS

const uploadToExternalBlob = async (
  file: File,
): Promise<ApiResponse<string>> => {
  const clinicId = localStorage.getItem('clinic_id')
  const formData = new FormData()
  formData.append('folder', FAX_ATTACHMENTS_FOLDER)
  if (clinicId) formData.append('clinic_id', clinicId)
  formData.append('file_name', file.name)
  formData.append('file', file)

  try {
    const response = await axios.post<ApiResponse<string>>(
      `${EXTERNAL_LAYER_API}/api/v1/external/azure/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  } catch (e: unknown) {
    const err = e as {
      response?: { status?: number; data?: ApiResponse<string> }
      message?: string
    }
    if (err.response?.data) {
      return err.response.data
    }
    throw toApiError(e, 'An unknown error occurred')
  }
}

// ——— Document ———
export const createDocument = async (
  data: IFaxDocument,
): Promise<ApiResponse<DocumentResponseData>> => {
  try {
    const response = await axios.post<ApiResponse<DocumentResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/document`,
      data,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in createDocument:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAllDocuments = async (
  dbName?: string,
): Promise<ApiResponse<DocumentListResponseData>> => {
  try {
    const response = await axios.get<ApiResponse<DocumentListResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/document/all`,
      { headers: getHeaders(dbName) },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllDocuments:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getDocumentById = async (
  id: string,
): Promise<ApiResponse<DocumentResponseData>> => {
  try {
    const response = await axios.get<ApiResponse<DocumentResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/document/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getDocumentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateDocumentById = async (
  fax_document_id: string,
  data: Partial<IFaxDocument>,
): Promise<ApiResponse<DocumentResponseData>> => {
  try {
    const response = await axios.put<ApiResponse<DocumentResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/document`,
      { fax_document_id, data },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateDocumentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteDocumentById = async (
  id: string,
): Promise<ApiResponse<GenericResponseData>> => {
  try {
    const response = await axios.delete<ApiResponse<GenericResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/document/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteDocumentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const uploadFaxFileToBlob = async (
  file: File,
): Promise<ApiResponse<FaxUploadMeta>> => {
  try {
    const blobResponse = await uploadToExternalBlob(file)

    if (blobResponse.statusCode >= 400 || !blobResponse.data) {
      throw new ApiError(
        blobResponse.statusCode || 500,
        blobResponse.message || 'Failed to upload file to storage.',
      )
    }

    return {
      statusCode: 201,
      message: 'Fax file uploaded successfully.',
      data: {
        file_url: blobResponse.data,
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_size: file.size,
      },
    }
  } catch (e: unknown) {
    logger.error('Error in uploadFaxFileToBlob:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const uploadFaxDocument = async (
  file: File,
  faxMessageId?: string,
  sortOrder?: number,
): Promise<ApiResponse<FaxUploadResponseData>> => {
  try {
    const blobResult = await uploadFaxFileToBlob(file)
    if (blobResult.statusCode >= 400 || !blobResult.data?.file_url) {
      throw new ApiError(
        blobResult.statusCode || 500,
        blobResult.message || 'Failed to upload file to storage.',
      )
    }

    const upload = blobResult.data
    const clinicId = localStorage.getItem('clinic_id')

    if (!faxMessageId) {
      return {
        statusCode: 201,
        message: 'Fax document uploaded successfully.',
        data: { upload },
      }
    }

    const docResponse = await createDocument({
      fax_message_id: faxMessageId,
      file_url: upload.file_url,
      file_name: upload.file_name,
      mime_type: upload.mime_type,
      file_size: upload.file_size,
      sort_order: sortOrder ?? 0,
      clinic_id: clinicId ?? '',
    })

    if (docResponse.statusCode >= 400) {
      throw new ApiError(
        docResponse.statusCode || 500,
        docResponse.message || 'Failed to save fax document.',
      )
    }

    return {
      statusCode: 201,
      message: 'Fax document uploaded successfully.',
      data: {
        upload,
        fax_document: docResponse.data?.fax_document,
      },
    }
  } catch (e: unknown) {
    logger.error('Error in uploadFaxDocument:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

type SignedUrlResponseData = { url: string | null }

export const getFaxFileSignedUrl = async (
  file_url: string,
): Promise<ApiResponse<SignedUrlResponseData>> => {
  try {
    const response = await axios.post<ApiResponse<SignedUrlResponseData>>(
      `${EXTERNAL_LAYER_API}/api/v1/external/azure/signed-url`,
      { url: file_url },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getFaxFileSignedUrl:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const downloadFile = async (
  fileUrl: string,
): Promise<ApiResponse<Blob>> => {
  try {
    const response = await axios.get<Blob>(fileUrl, { responseType: 'blob' })
    return {
      statusCode: response.status,
      message: response.statusText,
      data: response.data,
    }
  } catch (e: unknown) {
    logger.error('Error in downloadFile:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
